import React, { useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import {
  Briefcase, Users, MessageSquare, Settings, LogOut, Linkedin,
  Zap, ChevronRight, FileText, Search, BarChart3, Home, Lock, Activity,
  AlertTriangle, CreditCard, Crown, CloudLightning, Bell, Menu, X, PlayCircle,
} from "lucide-react";
import Logo from "./Logo";
import ProductTour from "./ProductTour";
import EmailVerifyBanner from "./EmailVerifyBanner";
import { useNotificationPoller, useNotifications } from "../hooks/useNotifications";

const AppLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isPro = user?.plan === "pro";
  const { permission, requestPermission, supported } = useNotifications();
  useNotificationPoller();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const freeItems = [
    { to: "/app/home", icon: Home, label: "Home" },
  ];

  const proItems = [
    { to: "/app/discover", icon: Search, label: "Discover Jobs" },
    { to: "/app/jobs", icon: Briefcase, label: "Jobs" },
    { to: "/app/network", icon: Users, label: "Network" },
    { to: "/app/inbox", icon: MessageSquare, label: "Inbox" },
    { to: "/app/templates", icon: FileText, label: "Templates" },
    { to: "/app/analytics", icon: BarChart3, label: "Analytics" },
    { to: "/app/autopilot", icon: CloudLightning, label: "Autopilot" },
    { to: "/app/activity", icon: Activity, label: "Activity" },
  ];

  // Bottom nav — most important items for mobile
  const bottomNavItems = [
    { to: "/app/home", icon: Home, label: "Home" },
    { to: "/app/jobs", icon: Briefcase, label: "Jobs" },
    { to: "/app/network", icon: Users, label: "Network" },
    { to: "/app/inbox", icon: MessageSquare, label: "Inbox" },
    { to: "/app/autopilot", icon: CloudLightning, label: "Autopilot" },
  ];

  const isHomePage = location.pathname === "/app/home" || location.pathname === "/app";

  const closeMobile = () => setMobileMenuOpen(false);

  return (
    <div className="app-container">
      {/* Mobile Header */}
      <div className="mobile-header" data-testid="mobile-header">
        <button onClick={() => setMobileMenuOpen(true)} className="mobile-menu-btn" data-testid="mobile-menu-btn">
          <Menu size={22} />
        </button>
        <Logo size="sm" />
        <div style={{ width: 36 }} /> {/* spacer */}
      </div>

      {/* Mobile backdrop */}
      {mobileMenuOpen && (
        <div
          className="mobile-backdrop"
          onClick={closeMobile}
          data-testid="mobile-backdrop"
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${mobileMenuOpen ? "open" : ""}`} style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
        <div className="logo" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Logo size="md" />
          <button
            onClick={closeMobile}
            className="mobile-close-btn"
            data-testid="mobile-close-btn"
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable nav area */}
        <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
        <nav className="nav-section">
          <div className="nav-section-title">Menu</div>
          {/* Free items — always accessible */}
          {freeItems.map((item) => (
            <NavLink key={item.to} to={item.to} className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`} onClick={closeMobile}>
              <item.icon size={18} />
              <span>{item.label}</span>
            </NavLink>
          ))}

          {/* Pro items — shown with lock if not pro */}
          {proItems.map((item) => (
            isPro ? (
              <NavLink key={item.to} to={item.to} className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`} onClick={closeMobile}>
                <item.icon size={18} />
                <span>{item.label}</span>
              </NavLink>
            ) : (
              <div key={item.to} className="nav-item locked" onClick={() => { navigate("/app/billing"); closeMobile(); }}>
                <item.icon size={18} />
                <span>{item.label}</span>
                <Lock size={12} style={{ marginLeft: "auto", opacity: 0.3 }} />
              </div>
            )
          ))}

          <div className="nav-section-title" style={{ marginTop: "1rem" }}>Account</div>
          <NavLink to="/app/linkedin-connect" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`} onClick={closeMobile} data-testid="nav-linkedin">
            <Linkedin size={18} />
            <span>Connection</span>
            {user?.linkedin_connected && <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--chill-success)", marginLeft: "auto" }} />}
          </NavLink>
          <NavLink to="/app/settings" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`} onClick={closeMobile} data-testid="nav-settings">
            <Settings size={18} />
            <span>Settings</span>
          </NavLink>
          <NavLink to="/app/billing" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`} onClick={closeMobile} data-testid="nav-billing">
            <CreditCard size={18} />
            <span>Billing</span>
          </NavLink>
          <a
            href="/learn"
            target="_blank"
            rel="noopener noreferrer"
            className="nav-item"
            onClick={closeMobile}
            data-testid="nav-learn"
            title="5 short video lessons (~15 min) on how to use ChillRefer"
          >
            <PlayCircle size={18} style={{ color: "#FACC15" }} />
            <span>Tutorials</span>
            <span style={{ marginLeft: "auto", fontSize: "0.6rem", padding: "1px 6px", background: "rgba(250,204,21,0.15)", color: "#FACC15", borderRadius: "9999px", fontWeight: 600 }}>
              NEW
            </span>
          </a>

          {/* Notification toggle */}
          {supported && permission !== "granted" && (
            <button
              onClick={requestPermission}
              className="nav-item w-full text-left"
              data-testid="nav-enable-notifications"
              style={{ color: "var(--chill-text-muted)" }}
            >
              <Bell size={18} />
              <span>Enable Notifications</span>
            </button>
          )}
          {supported && permission === "granted" && (
            <div className="nav-item" style={{ opacity: 0.5, cursor: "default" }}>
              <Bell size={18} style={{ color: "#22c55e" }} />
              <span>Notifications On</span>
            </div>
          )}

          <button onClick={() => { handleLogout(); closeMobile(); }} className="nav-item w-full text-left" data-testid="logout-btn">
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </nav>
        </div>{/* end scrollable */}

        {/* User info */}
        <div style={{ padding: "12px 16px", borderTop: "1px solid var(--chill-border)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--chill-bg)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem", fontWeight: 600 }}>
              {user?.name?.charAt(0) || "U"}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: "0.78rem", fontWeight: 600, margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{user?.name || "User"}</p>
              <p style={{ fontSize: "0.65rem", color: "#71717a", margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{user?.email}</p>
            </div>
            {isPro ? (
              <span style={{ fontSize: "0.6rem", padding: "1px 6px", background: "rgba(250,204,21,0.15)", color: "#FACC15", borderRadius: "9999px", fontWeight: 600 }} data-testid="pro-badge">
                PRO
              </span>
            ) : (
              <span style={{ fontSize: "0.6rem", padding: "1px 6px", background: "rgba(255,255,255,0.05)", color: "#52525b", borderRadius: "9999px", fontWeight: 600 }} data-testid="free-badge">
                FREE
              </span>
            )}
          </div>
        </div>

        {/* Upgrade banner for free users */}
        {!isPro && (
          <div
            onClick={() => { navigate("/app/billing"); closeMobile(); }}
            style={{ padding: "12px 16px", margin: "8px 12px", background: "rgba(250,204,21,0.06)", border: "1px solid rgba(250,204,21,0.15)", borderRadius: "6px", cursor: "pointer" }}
            data-testid="upgrade-banner"
          >
            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
              <Crown size={12} style={{ color: "#FACC15" }} />
              <p style={{ fontSize: "0.72rem", color: "#FACC15", fontWeight: 700, margin: 0 }}>Upgrade to Pro</p>
            </div>
            <p style={{ fontSize: "0.68rem", color: "#71717a", lineHeight: 1.4, margin: 0 }}>Unlock LinkedIn automation, job search, referral hunting & more</p>
          </div>
        )}
      </aside>

      <main className="main-content">
        {/* LinkedIn Disconnection Alert — Pro only */}
        {isPro && user?.linkedin_connected === false && user?.linkedin_account_id && (
          <div
            style={{
              display: "flex", alignItems: "center", gap: "10px",
              padding: "10px 20px", margin: "0",
              background: "rgba(239,68,68,0.08)", borderBottom: "1px solid rgba(239,68,68,0.2)",
            }}
            data-testid="linkedin-disconnected-alert"
          >
            <AlertTriangle size={16} style={{ color: "#ef4444", flexShrink: 0 }} />
            <p style={{ fontSize: "0.8rem", color: "#fafafa", margin: 0, flex: 1 }}>
              <strong style={{ color: "#ef4444" }}>LinkedIn Disconnected</strong> — Your session has expired.
            </p>
            <button
              onClick={() => navigate("/app/linkedin-connect")}
              style={{
                fontSize: "0.72rem", padding: "5px 14px",
                background: "#ef4444", color: "#fff", border: "none",
                borderRadius: "4px", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
              }}
              data-testid="reconnect-linkedin-btn"
            >
              Reconnect
            </button>
          </div>
        )}
        <div style={{ paddingBottom: "80px" }}> {/* space for bottom nav on mobile */}
          <div style={{ padding: isHomePage ? "0 1.5rem" : "1.5rem 1.5rem 0" }}>
            <EmailVerifyBanner />
          </div>
          {isHomePage ? <Outlet /> : <div style={{ padding: "1.5rem" }}><Outlet /></div>}
        </div>
      </main>

      {/* One-time guided product tour — auto-runs for newly-paid Pro users.
          Mounted at layout-level so it can navigate between /app/* pages. */}
      <ProductTour />

      {/* Bottom Navigation — Mobile only */}
      <nav className="bottom-nav" data-testid="bottom-nav">
        {bottomNavItems.map((item) => {
          const isActive = location.pathname === item.to;
          return (
            <NavLink
              key={item.to}
              to={isPro || item.to === "/app/home" ? item.to : "/app/billing"}
              className="bottom-nav-item"
              data-testid={`bottom-nav-${item.label.toLowerCase()}`}
              style={{ color: isActive ? "#FACC15" : "rgba(255,255,255,0.35)" }}
            >
              <item.icon size={20} />
              <span style={{ fontSize: "0.6rem", fontWeight: isActive ? 700 : 500 }}>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      {/* WhatsApp Support Button — hidden on mobile when bottom nav shows */}
      <a
        href="https://wa.me/917996325666?text=Hi%20Shubham%2C%20I%20need%20help%20with%20ChillRefer"
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-btn"
        data-testid="whatsapp-support-btn"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        <span className="whatsapp-text">Need help? Chat with the Founder</span>
      </a>
    </div>
  );
};

export default AppLayout;
